import { CalendarDays, CheckCircle, Clock, MapPin, Users, Video, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import { Badge } from "../../../components/ui/badge";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { formatDateTime } from "../../../utils/dateUtils";

const MeetingTable = ({
  meetings,
  permissions,
  onEdit,
  onDelete,
  onUpdateStatus,
  getStatusColor,
  getPriorityColor,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setPage
}) => {
  const meetingColumns = [
    {
      header: "Meeting",
      accessor: "title",
      isPrimary: true,
      render: (val, meeting) => (
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg shrink-0 ${getPriorityColor(meeting.priority)} bg-opacity-10`}>
            <Users className={`w-5 h-5 ${getPriorityColor(meeting.priority).replace("bg-", "text-")}`} />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100 truncate max-w-[200px]">
                {meeting.title}
              </span>
              {meeting.deleted && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                  Deleted
                </span>
              )}
            </div>
            {meeting.meetingType && (
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">{meeting.meetingType}</span>
            )}
          </div>
        </div>
      )
    },
    {
      header: "Schedule",
      accessor: "meetingDate",
      sortable: true,
      sortKey: "meetingDate",
      render: (val, meeting) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-sm text-yale-blue-700 dark:text-yale-blue-400 font-medium">
            <CalendarDays className="w-3.5 h-3.5" />
            {formatDateTime(meeting.meetingDate, meeting.meetingTime)}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {meeting.duration} mins
          </div>
        </div>
      )
    },
    {
      header: "Location",
      accessor: "location",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          {val?.toLowerCase().includes("zoom") || val?.toLowerCase().includes("meet") ? (
            <Video className="w-3.5 h-3.5 text-yale-blue-500" />
          ) : (
            <MapPin className="w-3.5 h-3.5 text-red-500" />
          )}
          <span className="truncate max-w-[150px]">{val || "Remote/Not set"}</span>
        </div>
      )
    },
    {
      header: "Attendees",
      accessor: "attendees",
      render: (val) => (
        <div className="flex -space-x-2 overflow-hidden">
          {val && val.length > 0 ? (
            <>
              {val.slice(0, 3).map((attendee, idx) => (
                <div
                  key={idx}
                  className="inline-block h-6 w-6 rounded-full ring-2 ring-white dark:ring-yale-blue-900 bg-slate-200 dark:bg-yale-blue-800 flex items-center justify-center text-[10px] font-medium text-yale-blue-700"
                  title={attendee}
                >
                  {attendee.substring(0, 1).toUpperCase()}
                </div>
              ))}
              {val.length > 3 && (
                <div className="flex items-center justify-center h-6 w-6 rounded-full ring-2 ring-white dark:ring-yale-blue-900 bg-slate-100 text-[10px] font-medium text-slate-500">
                  +{val.length - 3}
                </div>
              )}
            </>
          ) : (
            <span className="text-xs text-slate-400">No attendees</span>
          )}
        </div>
      )
    },
    {
      header: "Status",
      accessor: "status",
      render: (val) => <Badge className={`${getStatusColor(val)} whitespace-nowrap`}>{val.replace("_", " ")}</Badge>
    },
    {
      header: "Actions",
      filter: permissions?.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setPage(0);
                }}
                id="show-deleted-meetings-table"
              />
              <Label htmlFor="show-deleted-meetings-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, meeting) => (
        <ActionMenu
          canEdit={permissions?.canEdit && !meeting.deleted}
          canDelete={permissions?.canDelete && !meeting.deleted}
          onEdit={() => onEdit(meeting)}
          onDelete={() => onDelete(meeting.id)}
          customActions={
            meeting.status.toLowerCase() === "scheduled" && !meeting.deleted ? (
              <DropdownMenuItem className="cursor-pointer" onClick={() => onUpdateStatus(meeting.id, "completed")}>
                <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                <span>Complete</span>
              </DropdownMenuItem>
            ) : null
          }
        />
      )
    }
  ];

  return (
    <DataTable
      columns={meetingColumns}
      data={meetings}
      emptyStateTitle="No meetings found"
      emptyStateDescription="Schedule your first meeting to get started tracking project discussions."
      emptyStateIcon={CalendarDays}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default MeetingTable;
